import { useCallback } from 'react';
import { useMutation } from '@apollo/client';

import { CREATE_PERSON_DOCUMENT_UPLOAD } from '../mutations/PERSON_REGISTRATION';

export interface PendingDocument {
    kind: string;
    file: File;
}

/**
 * Upload supporting documents against a person.
 *
 * Two-step, like every other upload in this app: the server creates the row and hands back a
 * presigned PUT, the browser sends the bytes straight to S3.
 *
 * **The `Content-Type` header must match what was passed to the mutation.** The presign for
 * these signs content type (unlike the photo path, which signs `application/octet-stream` for
 * back-compatibility with old iOS builds), so a mismatched header is a SigV4 failure — and a
 * PDF that did sneak through as `application/octet-stream` would store fine and then refuse to
 * open in a browser.
 */
export const usePersonDocumentUpload = () => {
    const [createUpload] = useMutation(CREATE_PERSON_DOCUMENT_UPLOAD);

    return useCallback(async (personId: number, documents: PendingDocument[]) => {
        const failed: string[] = [];

        for (const { kind, file } of documents) {
            try {
                const { data } = await createUpload({
                    variables: {
                        personId,
                        kind,
                        fileName: file.name,
                        contentType: file.type,
                    },
                });

                const uploadUrl = data?.createPersonDocumentUpload?.uploadUrl;

                if (!uploadUrl) {
                    failed.push(file.name);
                    continue;
                }

                const response = await fetch(uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                    referrer: 'no-referrer',
                });

                if (!response.ok) {
                    failed.push(file.name);
                }
            } catch (e) {
                console.error('Document upload failed', e);
                failed.push(file.name);
            }
        }

        // Reported rather than thrown. By the time this runs the person has already been
        // created, and losing that save because an attachment failed would be the worse
        // outcome — the caller surfaces the names so the CM can retry from the profile.
        return failed;
    }, [createUpload]);
};
